// Client-side inventory-snapshot PDF via window.print(). V2 True Glass
// branding: deep obsidian black, champagne-gold accents, framed corners,
// Culinaire Specialist wordmark. Designed to look like a real operations
// document, not a spreadsheet screenshot.
//
// Currency is resolved from the incoming snapshot payload OR (for
// snapshots that were generated client-side without one) from the
// current user preference via prefs.getCurrency(). "$" is NEVER
// hardcoded. Format string uses Intl so "AUD" prints as "A$".

import { getCurrency } from "./prefs";

const FMT_MONEY = (v, currency) => {
  const cur = currency || getCurrency() || "USD";
  try {
    return Number(v || 0).toLocaleString(undefined, { style: "currency", currency: cur, maximumFractionDigits: 2 });
  } catch {
    return `${cur} ${(Number(v) || 0).toFixed(2)}`;
  }
};

export function printInventorySnapshot(snap) {
  const win = window.open("", "_blank", "width=1024,height=800");
  if (!win) return;

  const currency = snap.currency || getCurrency() || "USD";
  const generatedAt = snap.generated_at ? new Date(snap.generated_at) : new Date();
  const dateStr = generatedAt.toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
  const restaurant = snap.restaurant_name || snap.generated_by?.name || "Kitchen";
  const items = snap.items || [];
  const lowStock = snap.low_stock || items.filter((i) => i.below_par);
  const totalValue = FMT_MONEY(snap.total_inventory_value || 0, currency);

  const lowRows = lowStock.map((r) => `
      <tr>
        <td class="cell name">${r.name}</td>
        <td class="cell right muted">${r.on_hand} ${r.purchase_unit || ""}</td>
        <td class="cell right muted">${r.par_level} ${r.purchase_unit || ""}</td>
        <td class="cell right accent-bad">${r.suggested_order || (r.par_level - r.on_hand).toFixed(2)} ${r.purchase_unit || ""}</td>
      </tr>`).join("") || `<tr><td colspan="4" class="cell muted center">Every item is at or above its minimum stock level.</td></tr>`;

  const fullRows = items.map((r) => `
      <tr class="${r.below_par ? "row-low" : ""}">
        <td class="cell name">${r.name}${r.below_par ? '<span class="pill-low">LOW</span>' : ""}</td>
        <td class="cell right muted">${r.category || ""}</td>
        <td class="cell right">${r.on_hand} ${r.purchase_unit || ""}</td>
        <td class="cell right muted">${r.par_level || "-"} ${r.par_level ? (r.purchase_unit || "") : ""}</td>
        <td class="cell right tabular">${FMT_MONEY(r.est_value || 0, currency)}</td>
      </tr>`).join("");

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Inventory snapshot: ${restaurant}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=Inter+Tight:wght@400;500;600&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 0; }
  html, body { margin: 0; padding: 0; background: #0b0b0d; color: #f5efe0; font-family: 'Inter Tight', -apple-system, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet {
    box-sizing: border-box;
    width: 210mm; min-height: 297mm;
    padding: 16mm 18mm 20mm;
    background:
      radial-gradient(ellipse at top right, rgba(232,207,154,0.10), transparent 55%),
      linear-gradient(180deg, #0d0c0a 0%, #08070a 100%);
    position: relative;
  }
  .corner {
    position: absolute; width: 16mm; height: 16mm;
    border: 1.2px solid rgba(232,207,154,0.65);
  }
  .corner.tl { top: 8mm; left: 8mm; border-right: none; border-bottom: none; }
  .corner.tr { top: 8mm; right: 8mm; border-left: none; border-bottom: none; }
  .corner.bl { bottom: 8mm; left: 8mm; border-right: none; border-top: none; }
  .corner.br { bottom: 8mm; right: 8mm; border-left: none; border-top: none; }

  .brand {
    display: flex; align-items: center; gap: 8px;
    font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; color: #c8a96a;
  }
  .brand svg { width: 4mm; height: 4mm; color: #f2dfb5; }
  h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-weight: 400;
    font-size: 34px; line-height: 1.05; margin: 8mm 0 3mm;
    color: #f5efe0; letter-spacing: -0.01em;
  }
  .subtitle { font-family: 'Fraunces', serif; font-style: italic; font-size: 15px; color: #c8a96a; margin: 0; }
  .meta { display: flex; justify-content: space-between; margin-top: 10mm; font-size: 10px; color: #8a8578; letter-spacing: 0.16em; text-transform: uppercase; }
  .meta .value { color: #f2dfb5; text-transform: none; letter-spacing: 0; font-family: 'Fraunces', serif; font-size: 14px; margin-top: 1mm; }

  .value-hero {
    margin-top: 8mm; padding: 6mm 7mm;
    border-radius: 4mm;
    background: linear-gradient(180deg, rgba(232,207,154,0.10), rgba(232,207,154,0.02));
    border: 1px solid rgba(232,207,154,0.35);
  }
  .value-hero .eyebrow { font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase; color: #c8a96a; }
  .value-hero .amount {
    font-family: 'Fraunces', serif; font-weight: 400;
    font-size: 48px; letter-spacing: -0.02em; color: #f5efe0; margin-top: 2mm;
  }
  .value-hero .sub { font-size: 11px; color: #8a8578; margin-top: 1mm; }

  .section { margin-top: 8mm; }
  .section .label { font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase; color: #c8a96a; margin-bottom: 3mm; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 0; }
  thead th {
    text-align: left;
    padding: 2.5mm 3mm; font-size: 8.5px; letter-spacing: 0.2em; text-transform: uppercase; color: #6f6a5e;
    border-bottom: 1px solid rgba(232,207,154,0.25);
  }
  thead th.right { text-align: right; }
  .cell { padding: 2.2mm 3mm; font-size: 11px; border-bottom: 1px solid #1a1a1e; }
  .cell.right { text-align: right; }
  .cell.center { text-align: center; }
  .cell.muted { color: #8a8578; }
  .cell.name { color: #f5efe0; font-weight: 500; }
  .cell.tabular { font-variant-numeric: tabular-nums; }
  .pill-low {
    display: inline-block; margin-left: 6px; padding: 1px 6px;
    border-radius: 999px; background: #7a1f1a; color: #ffb9a8;
    font-size: 7.5px; letter-spacing: 0.14em; text-transform: uppercase;
    vertical-align: 1px; font-weight: 700;
  }
  .accent-bad { color: #ff9a7a; font-weight: 600; }
  .row-low td { background: rgba(220,120,90,0.05); }

  .low-callout {
    padding: 4mm 6mm; border-radius: 3mm;
    background: rgba(220,120,90,0.06);
    border: 1px solid rgba(220,120,90,0.35);
    margin-top: 2mm;
  }
  .low-callout .head { display:flex; justify-content: space-between; align-items:center; }
  .low-callout .count { font-family: 'Fraunces', serif; font-size: 26px; color: #e8a890; }

  .footer {
    position: absolute; left: 18mm; right: 18mm; bottom: 12mm;
    display: flex; justify-content: space-between; align-items: end;
    font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #6f6a5e;
  }
  .footer .value { color: #c8a96a; text-transform: none; letter-spacing: 0; font-family: 'Fraunces', serif; font-size: 12px; }

  .print-btn {
    position: fixed; top: 12px; right: 12px; z-index: 100;
    background: #c8a96a; color: #0b0b0d; border: none; padding: 9px 18px;
    border-radius: 999px; font-weight: 600; font-size: 12px; cursor: pointer;
    font-family: 'Inter Tight', sans-serif;
  }
  @media print { .print-btn { display: none; } }
</style></head>
<body>
  <button class="print-btn" onclick="window.print()">Print / Save as PDF</button>
  <div class="sheet">
    <div class="corner tl"></div><div class="corner tr"></div>
    <div class="corner bl"></div><div class="corner br"></div>

    <div class="brand">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"/>
        <line x1="6" y1="17" x2="18" y2="17"/>
      </svg>
      Culinaire Specialist • Operations
    </div>

    <h1>Inventory Snapshot</h1>
    <div class="subtitle">${restaurant}</div>

    <div class="meta">
      <div><div>Snapshot date</div><div class="value">${dateStr}</div></div>
      <div style="text-align:right;"><div>Prepared for</div><div class="value">${snap.generated_by?.name || restaurant}</div></div>
    </div>

    <div class="value-hero">
      <div class="eyebrow">Total inventory value</div>
      <div class="amount">${totalValue}</div>
      <div class="sub">Across ${snap.item_count || items.length} priced ingredients • currency ${currency}</div>
    </div>

    <div class="section">
      <div class="label">Below minimum stock • ${lowStock.length}</div>
      <div class="low-callout">
        <table>
          <thead>
            <tr>
              <th>Ingredient</th>
              <th class="right">On hand</th>
              <th class="right">Min level</th>
              <th class="right">Suggested order</th>
            </tr>
          </thead>
          <tbody>${lowRows}</tbody>
        </table>
      </div>
    </div>

    ${items.length > 0 ? `
    <div class="section">
      <div class="label">Full inventory</div>
      <table>
        <thead>
          <tr>
            <th>Ingredient</th>
            <th class="right">Category</th>
            <th class="right">On hand</th>
            <th class="right">Min level</th>
            <th class="right">Value</th>
          </tr>
        </thead>
        <tbody>${fullRows}</tbody>
      </table>
    </div>` : ""}

    <div class="footer">
      <div>Culinaire Specialist • culinairespecialist.com</div>
      <div style="text-align:right;">Generated <span class="value">${dateStr}</span></div>
    </div>
  </div>
  <script>
    window.addEventListener('load', () => setTimeout(() => { try { window.print(); } catch(_){} }, 500));
  </script>
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}
